import { StickyNote } from 'lucide-react';
import { stripHtml } from '@/common/utils/stripHtml';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { ACTION_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import {
  collectErrors,
  validateTemplateChunks,
  validateVariableRef,
} from '../../validation';
import { defaultNodeData } from '../../graphUtils';
import { formatTemplateChunks } from '../../variables/variableUtils';
import { AddCocNoteActionCustomization } from './AddCocNoteActionCustomization';

/** Validation field names for Add note to CoC. Shared with the UI. */
export const AddCocNoteActionFields = {
  SHIPMENT: 'Shipment',
  MESSAGE_TEXT: 'Message text',
} as const;

export const AddCocNoteActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'add_coc_note_action',

  category: NodeCategory.ACTION,

  label: 'Add note to CoC',

  hasInput: true,

  requiresShipmentInContext: true,

  getIcon: (size: number) => (
    <StickyNote size={size} color={ACTION_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, graph: GraphData) => {
    const messageChunks = data.add_coc_note_action?.message_chunks ?? [];
    const audience = data.add_coc_note_action?.audience;
    const teamIds = data.add_coc_note_action?.team_ids || [];

    let message: string | null = null;
    if (messageChunks.length > 0) {
      // Each text chunk holds an HTML fragment. Its markup is stripped before the
      // labels are joined in. A label holding `<` or `&` then reads as written.
      const plainChunks = messageChunks.map((chunk) =>
        chunk.text !== undefined ? { text: stripHtml(chunk.text) } : chunk
      );
      message = formatTemplateChunks(plainChunks, graph.nodes).trim();
    }

    let subtitle = '(no message)';
    if (message !== null) {
      subtitle = message.length > 30 ? message.slice(0, 30) + '...' : message;
    }

    if (audience === 'my_team') {
      subtitle += ' (My Team)';
    } else if (audience === 'specific_teams' && teamIds.length > 0) {
      subtitle += ` (${teamIds.length} team${teamIds.length > 1 ? 's' : ''})`;
    }

    return subtitle;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const d = nodeData.add_coc_note_action;
    return collectErrors(
      validateVariableRef(
        d?.shipment_id,
        nodeId,
        graph,
        AddCocNoteActionFields.SHIPMENT
      ),
      ...validateTemplateChunks(
        d?.message_chunks ?? [],
        nodeId,
        graph,
        AddCocNoteActionFields.MESSAGE_TEXT
      )
    );
  },

  CustomizationComponent: AddCocNoteActionCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('add_coc_note_action', {
      shipment_id: undefined,
      message_chunks: [],
      audience: 'public',
      team_ids: [],
      mentioned_user_ids: [],
      append_to_alert: false,
    }),
};
